// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The last tier this browser was told about (M10-12).
//
// WHY A SIGNAL RATHER THAN A FETCH
// --------------------------------
// The headset entry lives on the **front door** — the page a visitor plays NEON NOVA on with no
// account at all — and FR-030 means nothing that page loads may reach the network or construct a
// Supabase client. So the stage cannot ask what tier the viewer is; the answer has to have arrived
// already.
//
// The account bundle writes here whenever `/entitlement` answers, and the front door reads. Exactly
// the shape `setImmersive`/`isImmersive` already uses in `useAccount.ts`, and for the same reason:
// one fact, needed on both sides of a boundary that only one side may cross.
//
// WHAT THIS IS AND IS NOT
// -----------------------
// **A hint that decides what is offered, never an authority about what is allowed.** It is in
// `localStorage`, which the player owns and can edit, so it is trusted for exactly one thing:
// whether to show a paying subscriber the Enter VR button on a cold tab instead of sending them to
// a pricing page they do not need.
//
// Everything a forged value could reach is refused by the **server** regardless — the shared arcade
// by `pinball.may_enter_arcade` and a 402 on `voice.ice`, publishing by a 402 on
// `machines.publish`, the roster by a 402 on `machines.version`. What editing this buys somebody is
// a solo WebXR session of a machine they can already play in a browser window, which is not a thing
// worth building a second gate for.
//
// **Deliberately not the whole entitlement.** No expiry, no provider, no features: a tier and
// nothing else, because a richer copy here would be a second projection to keep in step with the
// one the server computes.

import type { Tier } from '@src/ui/pricingCatalog'

const KEY = 'pinball.tier'

const TIERS: readonly string[] = ['anonymous', 'free', 'pro']

/**
 * What this browser was last told, or `undefined` if it has never been told.
 *
 * `undefined` and `'anonymous'` are different answers and the difference matters: never asked is a
 * cold tab, and anonymous is a visitor who has asked and has no account. A caller that treated them
 * alike would offer an account to somebody in the middle of signing in.
 */
export function knownTier(): Tier | undefined {
  try {
    const stored = globalThis.localStorage?.getItem(KEY)
    return stored !== null && stored !== undefined && TIERS.includes(stored) ? (stored as Tier) : undefined
  } catch {
    // Private browsing, a blocked cookie policy, a sandboxed iframe. Not knowing is a state this
    // module already has a value for, so there is nothing to report.
    return undefined
  }
}

/** Record what the server said. Called by the account bundle, never by the front door. */
export function rememberTier(tier: Tier): void {
  try {
    globalThis.localStorage?.setItem(KEY, tier)
  } catch {
    // Nothing to do and nothing worth saying: the next page load asks again.
  }
}

/**
 * Forget it.
 *
 * **On sign-out, without exception.** Leaving `pro` behind after somebody signs out would offer the
 * next person at that browser a Pro control, and the refusal they would get from the server is a
 * worse first impression than not having been offered.
 */
export function forgetTier(): void {
  try {
    globalThis.localStorage?.removeItem(KEY)
  } catch {
    // As above.
  }
}
